import React from "react";
import { getComparisonSuffix } from "@common/helpers/reports";
import { formatMoneyColumnHeader } from "@common/helpers/number";
import { NumberFormatValues } from "@common/schemas/domains/reportSettings/number-format-setting.schema";
import { ReportPeriodWithLabel } from "@common/types/reports/types";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

function renderClassColumnHeader(
    label: string,
    tooltip: string,
): React.ReactElement {
    return (
        <Tooltip side="bottom" content={tooltip}>
            <span>{label}</span>
        </Tooltip>
    );
}

/**
 * Header renderer for Direct, Total, and No class value columns. Returns
 * undefined for leaf columns so they keep the default class-name header, and
 * for diff/ratio columns so they keep their own self-describing label
 * (e.g. "% Change (PY)").
 */
export function makeClassColumnHeaderCell(
    period: ReportPeriodWithLabel,
    numberFormat?: NumberFormatValues,
): (() => React.ReactElement) | undefined {
    const { classColumnRole: role, comparisonType } = period;

    if (period.comparisonDiff || period.ratioType) {
        return undefined;
    }

    const className = period.classHeaderPath?.at(-1)?.label ?? "";
    const suffix = comparisonType ? getComparisonSuffix(comparisonType) : "";

    if (role === "parentDirect") {
        const DirectHeader = () =>
            renderClassColumnHeader(
                formatMoneyColumnHeader(`Direct${suffix}`, numberFormat),
                `Activity posted to ${className}, not to a subclass.`,
            );
        DirectHeader.displayName = "ClassDirectHeader";
        return DirectHeader;
    }

    if (role === "unclassified") {
        const NoClassHeader = () =>
            renderClassColumnHeader(
                formatMoneyColumnHeader(period.label, numberFormat),
                "Activity with no class assigned.",
            );
        NoClassHeader.displayName = "ClassNoClassHeader";
        return NoClassHeader;
    }

    if (role === "parentTotal") {
        const TotalHeader = () =>
            renderClassColumnHeader(
                formatMoneyColumnHeader(`Total${suffix}`, numberFormat),
                `Total for ${className}, including all subclasses`,
            );
        TotalHeader.displayName = "ClassTotalHeader";
        return TotalHeader;
    }

    return undefined;
}
